import type { SwitchboardHealthCall, SwitchboardHealthResponse } from "@t3tools/contracts";
import {
  ActivityIcon,
  AudioLinesIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { ChartPanel, KpiCard, KpiGrid } from "./dashboardKit";
import { switchboardLabel } from "./switchboardOutcomes";

const number = (value: number) => value.toLocaleString();
function percent(part: number, total: number) {
  return total ? `${((part / total) * 100).toFixed(1)}%` : "—";
}
function seconds(value: number | null) {
  return value === null ? "—" : `${value.toFixed(2)}s`;
}
function time(value: string | null, timezone: string) {
  return value
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "medium",
      }).format(new Date(value))
    : "Not recorded";
}
const buttonClass =
  "rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-40";

function CallDiagnostics({ call, timezone }: { call: SwitchboardHealthCall; timezone: string }) {
  return (
    <details className="group border-b border-border/60 last:border-b-0">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 px-4 py-3 hover:bg-accent/40 [&::-webkit-details-marker]:hidden">
        <ChevronRightIcon
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
          aria-hidden
        />
        <div className="min-w-40 flex-1">
          <span className="text-sm font-medium">{call.carespaceName}</span>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {call.navigatorName} · {time(call.dialedAt, timezone)}
          </p>
        </div>
        <span className="text-xs text-muted-foreground">
          {call.eventCount} events · {switchboardLabel(call.nativeStatus ?? call.outcome)}
        </span>
        <span
          className={
            call.issues.length
              ? "rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200"
              : "rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground"
          }
        >
          {call.issues.length ? `${call.issues.length} review signals` : "No review flags"}
        </span>
      </summary>
      <div className="space-y-4 border-t border-border/50 bg-muted/20 p-4 text-xs">
        {call.issues.length ? (
          <ul className="flex flex-wrap gap-2" aria-label="Review signals">
            {call.issues.map((issue) => (
              <li
                key={issue}
                className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
              >
                {issue}
              </li>
            ))}
          </ul>
        ) : null}
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              ["Native call ID", call.nativeCallId],
              ["Daily room", call.roomName],
              ["Daily session", call.dailySessionId],
              ["Room ready", call.roomReady === null ? null : call.roomReady ? "Yes" : "No"],
              [
                "Dial state / code",
                [call.dialStatus, call.errorCode].filter(Boolean).join(" · ") || null,
              ],
              [
                "Worker",
                call.workerStatus ? `${call.workerStatus} · ${call.workerErrors} error runs` : null,
              ],
              ["Recording", call.recordingStatus],
              [
                "Recording ready",
                call.recordingReadyAt ? time(call.recordingReadyAt, timezone) : null,
              ],
              [
                "Event receipt p95",
                call.p95ReceiptSeconds === null ? null : seconds(call.p95ReceiptSeconds),
              ],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="mt-1 break-all font-medium">{value ?? "Not recorded"}</dd>
            </div>
          ))}
        </dl>
        <div className="grid gap-2 rounded-lg border border-border bg-background p-3 sm:grid-cols-2 lg:grid-cols-5">
          {(
            [
              ["Dial attempted", call.attemptedAt],
              ["Provider answered", call.answeredAt],
              ["Handoff connected", call.handoffAt],
              ["Staff joined", call.staffJoinedAt],
              ["Dial ended", call.endedAt],
            ] as const
          ).map(([label, value]) => (
            <div key={label}>
              <p className="text-muted-foreground">{label}</p>
              <p className="mt-1">{time(value, timezone)}</p>
            </div>
          ))}
        </div>
        <div>
          <h4 className="font-semibold">Stored provider events</h4>
          <p className="mt-1 text-muted-foreground">
            Occurred and received times in {timezone}. Showing {call.events.length} of{" "}
            {call.eventCount} events; most recent 50, in time order.
          </p>
          {call.events.length ? (
            <ol className="mt-3 max-h-64 space-y-2 overflow-auto border-l-2 border-sky-200 pl-3 dark:border-sky-900">
              {call.events.map((event) => (
                <li
                  key={event.id}
                  className="flex flex-wrap justify-between gap-1 rounded bg-background px-3 py-2"
                >
                  <div>
                    <p className="font-medium">{event.type}</p>
                    <p className="mt-1 text-muted-foreground">
                      Occurred {time(event.occurredAt, timezone)} · received{" "}
                      {time(event.receivedAt, timezone)}
                    </p>
                  </div>
                  <span
                    className={
                      event.receiptSeconds < 0 || event.receiptSeconds > 5
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-muted-foreground"
                    }
                  >
                    {seconds(event.receiptSeconds)} receipt delay
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-3 text-muted-foreground">No provider events recorded for this call.</p>
          )}
        </div>
      </div>
    </details>
  );
}

export function SwitchboardDailyHealth({
  data,
  onPage,
  fetching,
}: {
  data: SwitchboardHealthResponse;
  onPage: (page: number) => void;
  fetching: boolean;
}) {
  const s = data.summary;
  const max = Math.max(1, ...data.daily.map((day) => day.attempts));
  const pages = Math.max(1, Math.ceil(s.totalAttempts / data.pageSize));
  return (
    <div className="space-y-5">
      <section
        aria-label="Daily call health"
        className="overflow-hidden rounded-xl border border-sky-200/70 bg-gradient-to-br from-sky-50 via-background to-indigo-50 p-5 dark:border-sky-900 dark:from-sky-950/40 dark:to-indigo-950/30"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sky-700 dark:text-sky-300">
              <ActivityIcon className="size-4" aria-hidden />
              <p className="text-xs font-semibold tracking-wide uppercase">
                Provider observability
              </p>
            </div>
            <h2 className="mt-2 text-xl font-semibold tracking-tight">Daily call health</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Follow event evidence, receipt delays, and failures through each call.
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-2 text-xs font-medium">
            {s.reviewCalls ? (
              <TriangleAlertIcon className="size-4 text-amber-600" aria-hidden />
            ) : (
              <CheckCircle2Icon className="size-4 text-muted-foreground" aria-hidden />
            )}
            {s.reviewCalls
              ? `${number(s.reviewCalls)} calls with review signals`
              : s.totalAttempts
                ? "No review flags"
                : "No observations"}
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {data.period.dateStart} – {data.period.dateEnd} · {number(s.totalAttempts)} attempts ·{" "}
          {data.timezone} · refreshes every minute while open
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Latest stored event received: {time(s.latestEventReceivedAt, data.timezone)}
        </p>
        <KpiGrid className="mt-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Participant event coverage"
            value={percent(s.eventCoveredCalls, s.eventExpectedCalls)}
            footnote={`${number(s.eventCoveredCalls)} of ${number(s.eventExpectedCalls)} eligible ended calls`}
            tooltip="Both join and leave events recorded for calls with a Daily room and agent-ready, answer, or staff-join evidence. Only calls ended at least 5 minutes ago are eligible. Missing evidence is a review signal, not proof of a lost webhook."
          />
          <KpiCard
            label="Event receipt p95"
            value={seconds(s.p95ReceiptSeconds)}
            footnote={`${number(s.delayedEvents)} events over 5s · ${number(s.observedEvents)} stored`}
            tooltip="95th percentile of received_at minus occurred_at across stored events in this call cohort. Negative clock differences are flagged and excluded. This is receipt delay, not processing latency or an SLA."
          />
          <KpiCard
            label="Dial failure rate"
            value={percent(s.dialFailures, s.dialAttempts)}
            footnote={`${number(s.dialFailures)} of ${number(s.dialAttempts)} actual dials`}
            tooltip="Provider dial attempts with a failed status or stored error code. Preparation failures are counted separately; busy, no answer, and voicemail are not technical failures by themselves."
          />
          <KpiCard
            label="Calls to review"
            value={number(s.reviewCalls)}
            footnote={`${number(s.staleCalls)} exceed state review thresholds`}
            tooltip="Distinct calls with one or more review signals. Thresholds: starting over 5 minutes; ending more than 5 minutes after recorded native call end; active over 2 hours; saved calling state unresolved more than 5 minutes after native end. Ending duration is unknown when the native end timestamp is missing. Signals can overlap and need investigation."
          />
        </KpiGrid>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
          <span>
            Recording ready{" "}
            <strong className="text-foreground">
              {s.recordingReadyCalls}/{s.recordingExpectedCalls}
            </strong>{" "}
            eligible requested calls
          </span>
          <span>
            Recording failures{" "}
            <strong className="text-foreground">{s.recordingFailureCalls}</strong>
          </span>
          <span>
            Worker error calls <strong className="text-foreground">{s.workerErrorCalls}</strong>
          </span>
          <span>
            Preparation failures{" "}
            <strong className="text-foreground">{s.preparationFailures}</strong>
          </span>
        </div>
      </section>
      <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <ChartPanel title="Review signals over time">
          <div className="mb-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-amber-500" />
              With review signals
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-sky-400" />
              No review flags
            </span>
          </div>
          {data.daily.length ? (
            <div className="overflow-x-auto">
              <div
                className="flex h-44 min-w-full items-end gap-2 pb-6"
                style={{ minWidth: data.daily.length * 35 }}
                role="img"
                aria-label="Daily attempts split by review signals"
              >
                {data.daily.map((day) => (
                  <div
                    key={day.date}
                    className="relative flex h-full min-w-6 flex-1 flex-col justify-end"
                    title={`${day.date}: ${day.attempts} attempts, ${day.reviewCalls} to review, ${day.eventGaps} event gaps`}
                  >
                    <div
                      className="flex w-full flex-col overflow-hidden rounded-t"
                      style={{ height: `${(day.attempts / max) * 100}%` }}
                    >
                      <div
                        className="bg-amber-500"
                        style={{ height: `${(day.reviewCalls / day.attempts) * 100}%` }}
                      />
                      <div className="min-h-0 flex-1 bg-sky-400" />
                    </div>
                    <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted-foreground">
                      {day.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No attempts in this scope.
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Dates with attempts are shown. Bar height represents attempts.
          </p>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-muted-foreground">View daily counts</summary>
            <div className="mt-2 max-h-48 overflow-auto">
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className="py-1">Date</th>
                    <th>Attempts</th>
                    <th>Review</th>
                    <th>Event gaps</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((day) => (
                    <tr key={day.date}>
                      <td className="py-1">{day.date}</td>
                      <td>{day.attempts}</td>
                      <td>{day.reviewCalls}</td>
                      <td>{day.eventGaps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </ChartPanel>
        <ChartPanel title="Failures by stage">
          <p className="mb-3 text-xs text-muted-foreground">
            Affected calls; a call can appear in more than one stage.
          </p>
          {data.failures.length ? (
            <ul className="max-h-64 space-y-3 overflow-auto">
              {data.failures.map((failure) => (
                <li
                  key={`${failure.stage}:${failure.code}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/70 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold">{failure.stage}</p>
                    <p className="mt-0.5 break-words text-xs text-muted-foreground">
                      {failure.code}
                    </p>
                  </div>
                  <span className="rounded-md bg-muted px-2 py-1 text-sm font-semibold tabular-nums">
                    {failure.calls}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No failures recorded in this scope.
            </p>
          )}
        </ChartPanel>
      </div>
      <section
        aria-label="Observability coverage"
        className="grid gap-3 rounded-lg border border-border bg-muted/20 p-4 text-xs sm:grid-cols-3"
      >
        <div>
          <p className="font-semibold">Stored events · available</p>
          <p className="mt-1 leading-relaxed text-muted-foreground">
            Matched participant and recording events, dial codes, worker and recording states.{" "}
            {s.eventGapCalls} eligible calls have event gaps; {s.clockSkewEvents} events have clock
            inconsistencies.
          </p>
        </div>
        <div>
          <p className="font-semibold">Full delivery ledger · unavailable</p>
          <p className="mt-1 leading-relaxed text-muted-foreground">
            Rejected or unmatched webhooks and processing retries are not captured here. No review
            flags does not establish complete delivery.
          </p>
        </div>
        <div>
          <p className="flex items-center gap-1.5 font-semibold">
            <AudioLinesIcon className="size-3.5" aria-hidden />
            Audio quality · not collected
          </p>
          <p className="mt-1 leading-relaxed text-muted-foreground">
            Packet loss, jitter, and audio quality scores are not stored. Provider answer timestamps
            do not establish a real conversation.
          </p>
        </div>
      </section>
      <ChartPanel title="Call diagnostics">
        <p className="mb-3 text-xs text-muted-foreground">
          Review signals first, then newest attempt. Expand a call for room/session identifiers and
          event timing. Review thresholds allow 5 minutes after call end for event and recording
          arrival.
        </p>
        <div className="max-h-[40rem] overflow-auto rounded-lg border border-border">
          {data.calls.length ? (
            data.calls.map((call) => (
              <CallDiagnostics key={call.id} call={call} timezone={data.timezone} />
            ))
          ) : (
            <p className="p-8 text-center text-sm text-muted-foreground">No calls in this scope.</p>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            {s.totalAttempts
              ? `${Math.min((data.page - 1) * data.pageSize + 1, s.totalAttempts)}–${Math.min(data.page * data.pageSize, s.totalAttempts)} of ${number(s.totalAttempts)} attempts`
              : "0 attempts"}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={buttonClass}
              disabled={fetching || data.page <= 1}
              onClick={() => onPage(data.page - 1)}
            >
              Previous
            </button>
            <span>
              Page {data.page} of {pages}
            </span>
            <button
              type="button"
              className={buttonClass}
              disabled={fetching || data.page >= pages}
              onClick={() => onPage(data.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </ChartPanel>
    </div>
  );
}
